<template>
  <ValidatePassword
    ref="passwordDialog"
    custom-endpoint
    autocomplete
    @submit="tryLogin"
    @hide="hide"
  >
    <span>Your session is about to expire. Please re-enter your password to remain logged in or alternatively switch accounts.</span>

    <template #buttons>
      <q-btn
        flat
        label="Switch Accounts"
        color="blue-grey"
        no-caps
        @click="hide"
      />
    </template>
  </ValidatePassword>
</template>

<script>
import ValidatePassword from 'components/partials/ValidatePassword.vue'
import auth from 'components/auth'
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { getBiometricKey, forgetBiometricKey } from 'src/helpers/biometricLogin'
import { refreshRegistration } from 'src/notifications/push'

// This component is used for dynamic calls for reauthorizing the user's session
// Refer to https://quasar.dev/quasar-plugins/dialog#Invoking-custom-component
export default {
  components: {
    ValidatePassword
  },
  emits: [
    // REQUIRED
    'ok', 'hide'
  ],
  data () {
    return {
      retryCounter: 0
    }
  },
  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    async show () {
      const key = this.$q.platform.is.nativeMobile && this.$store.getters.getPortal === 'business'
        ? await getBiometricKey(this.userData?.email)
        : null

      if (key) {
        await this.tryBiometricLogin(key)
        return
      }

      this.$refs.passwordDialog.open()
    },
    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide () {
      this.$refs.passwordDialog.close()
      this.$emit('hide')
    },
    // A device with a biometric key re-authenticates without a password; any failure falls back to the password dialog
    async tryBiometricLogin ({ token, guid }) {
      try {
        await BiometricAuth.authenticate({
          reason: 'Authenticate to stay signed in',
          allowDeviceCredential: true
        })
      }
      catch {
        this.$refs.passwordDialog.open()
        return
      }

      auth.login('business-biometric', { token, guid }, (newToken) => {
        this.onOKClick(newToken)
        this.$emit('hide')
        refreshRegistration()
      }, async (errors) => {
        if (errors?.biometric) {
          await forgetBiometricKey(this.userData?.email)
        }
        this.$refs.passwordDialog.open()
      })
    },
    tryLogin ({ loading, done, submit, onError }) {
      loading()
      const portal = this.$store.getters.getPortal
      let endpoint = null
      switch (portal) {
        case 'business': endpoint = 'business-login'; break
        case 'user': endpoint = 'client-connect-login'; break
      }
      auth.login(endpoint, submit(), (token) => {
        this.retryCounter = 0
        this.onOKClick(token)
        this.$refs.passwordDialog.close()
        refreshRegistration()
      }, (errors, message) => {
        if (this.retryCounter >= 2) {
          this.hide()
          return
        }
        this.retryCounter++
        onError(message, {
          password: ['Your password is incorrect']
        })
        done()
      })
    },
    onOKClick (token = null) {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok', token)
    },
    onCancelClick () {
      // we just need to hide dialog
      this.hide()
    }
  }
}
</script>
